/**
 * Setup Wizard — the single onboarding wizard for users in `pending_setup`.
 *
 * Branches on `setup-wizard/options` by flow:
 *  - **create** — brand-new tenant: Company → Profile → Modules → Demo data.
 *    Prefilled from the AI enrichment payload (`/prefill`) with per-field
 *    confidence driving "AI suggested" badges.
 *  - **join** (Flow B) — invitation / domain-match (self-registration ON): a
 *    single Profile step, then admit into the existing tenant.
 *  - **request** — domain match with self-registration OFF: request-to-join with
 *    admin approval, landing on the pending-approval screen.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AUTH_STORAGE_KEYS, useAuth, authApi } from '../../shared/auth';
import { clearRefreshToken, setRefreshToken } from '../../shared/auth/secureTokenStore';
import { useNotification } from '../../shared/context';
import { getBrowserTimezone } from '../../shared/utils/timezone';
import { toTenantDefaultLanguage } from '../../i18n/tenantDefaultLanguage';
import { getGravatarUrl } from '../../shared/utils';
import { Card, CardContent } from '../../components/ui/card';
import { LanguageSelector } from '../../components/ui/language-selector';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Loader2, ArrowRight, Info } from 'lucide-react';
import { AIProcessingBanner } from '../../shared/components/common';
import { getErrorMessage } from '../../shared/utils/errors';
import {
  setupWizardApi,
  type SetupModule,
  type SetupWizardPrefill,
  type PrefillPersonCandidate,
  type CreateTenantRequest,
  type DomainMatchRequestOption,
  type MyJoinRequest,
} from '../../shared/api/setupWizardApi';
import { ApplicationReceivedScreen } from './ApplicationReceivedScreen';
import { JoinRequestPendingScreen } from './JoinRequestPendingScreen';
import { WizardStepper, type WizardStep } from './setup-wizard-v2/WizardStepper';
import {
  CompanyStep,
  CompanyStepSkeleton,
  type CompanyFormState,
  type CompanyConfidence,
  type CompanyField,
} from './setup-wizard-v2/CompanyStep';
import { ProfileStep, type ProfileFormState } from './setup-wizard-v2/ProfileStep';
import { JoinProfileStep } from './setup-wizard-v2/JoinProfileStep';
import { ModulesStep } from './setup-wizard-v2/ModulesStep';
import { DemoDataStep, type DemoDataFormState } from './setup-wizard-v2/DemoDataStep';
import { CompanyLookupPrompt } from './setup-wizard-v2/CompanyLookupPrompt';
import {
  mapCompanyPrefill,
  classifyPrefill,
  type PrefillOutcome,
} from './setup-wizard-v2/companyPrefill';
import {
  deriveSellsFlags,
  selectedBusinessModels,
  EMPTY_SELLS_FLAGS,
  FALLBACK_MODULE_IDS,
  buildVisibilityModesPayload,
  defaultVisibilityModeForBand,
} from './setup-wizard-v2/options';
import { WizardLogoutButton } from './components/WizardLogoutButton';
import { STANDALONE_TITLE } from '../../shared/styles/typography';

const EMPTY_COMPANY: CompanyFormState = {
  name: '',
  website: '',
  country: null,
  industry: '',
  customerTypes: [],
  sizeBand: null,
  description: '',
  logoFileId: null,
  brandColor: null,
  sellsFlags: { ...EMPTY_SELLS_FLAGS },
  operatingCountries: [],
  isGlobalOperation: false,
  signupReason: '',
  referralSource: null,
  referralSourceOther: '',
  expectedUsers: null,
};

const EMPTY_PROFILE: ProfileFormState = {
  jobFunction: '',
  jobFunctionCustom: '',
  jobLevel: '',
  linkedinUrl: '',
  headline: '',
  photoUrl: null,
  avatarFileId: null,
  photoFile: null,
};

type Mode = 'loading' | 'create' | 'join' | 'request';

/** Resolved target for Flow B (joiner short flow): which tenant/company the
 *  user is admitted into, plus the server-authoritative admin flag. */
interface JoinTarget {
  tenantId: string;
  /** Null for an invitation that names no company — the join resolves the tenant default. */
  companyId: string | null;
  isAdmin: boolean;
  tenantName: string;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Read a picked image into the `data:image/...;base64,...` URL the API expects. */
const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const PREFILL_POLL_INTERVAL_MS = 500;

/**
 * Wall-clock per attempt isn't just `PREFILL_POLL_INTERVAL_MS`: while
 * enrichment is pending, `/prefill` itself short-polls Redis server-side for up
 * to `_PREFILL_POLL_BUDGET_SECONDS` (3s) before responding. So an attempt costs
 * up to ~3.5s — an estimate driven by the backend, not something enforced here.
 * The two counts below are sized against that estimate; neither is a deadline.
 */

/** How long Step 1 stays behind the prefill spinner (≲6 × 3.5s ≈ 21s). */
const PREFILL_SPINNER_ATTEMPTS = 6;

/**
 * How long we keep polling *after* the spinner gives up. Registration-time
 * enrichment gets a 45s wall-clock budget (`REGISTRATION_TIMEOUT_SECONDS`), so
 * stopping at the spinner ceiling would latch `prefillApplied` and discard a
 * payload that was still on its way — the "wizard renders a mostly-empty
 * Step 1" symptom. ≲14 × 3.5s ≈ 49s covers the full backend budget.
 *
 * A payload landing in this second phase is applied under the already-rendered
 * form; `applyPrefill` skips any field the user has touched, and skips the
 * lookup-mode switch entirely once they've started typing.
 */
const PREFILL_POLL_ATTEMPTS = 14;

export function SetupWizardPageV2() {
  const { t, i18n } = useTranslation(['user_management', 'common']);
  const navigate = useNavigate();
  const { refreshAuth, user } = useAuth();
  const { showSuccess, showError } = useNotification();

  const accessToken = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken) || '';

  const [mode, setMode] = useState<Mode>('loading');
  // Flow B (joiner) target, resolved from setup-wizard/options on load.
  const [joinTarget, setJoinTarget] = useState<JoinTarget | null>(null);
  // Request-to-join (domain match, self-registration OFF): the tenant the user
  // can ask to join, the optional preferred company, and the user's outstanding
  // request (short-circuits the wizard to the pending-approval screen).
  const [requestTarget, setRequestTarget] = useState<DomainMatchRequestOption | null>(null);
  const [requestCompanyId, setRequestCompanyId] = useState<string>('');
  const [pendingJoinRequest, setPendingJoinRequest] = useState<MyJoinRequest | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Sysadmin tenant-approval gate (WI-1). When on, the create step reads as an
  // application and a gated submission lands on the "application received" screen.
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Prefill
  const [prefillLoading, setPrefillLoading] = useState(true);
  const [prefillOutcome, setPrefillOutcome] = useState<PrefillOutcome>('pending');
  const [candidates, setCandidates] = useState<PrefillPersonCandidate[]>([]);
  const [bestCandidateId, setBestCandidateId] = useState<string | null>(null);
  const [gravatarUrl, setGravatarUrl] = useState<string | null>(null);
  const [srMessage, setSrMessage] = useState('');

  // Company
  const [company, setCompany] = useState<CompanyFormState>(EMPTY_COMPANY);
  const [companyConfidence, setCompanyConfidence] = useState<CompanyConfidence>({});
  const editedCompany = useRef<Set<string>>(new Set());
  // Step 1 sub-mode: 'form' is the standard Confirm-Company UI; 'lookup' is the
  // generic-domain ask-+-fetch prompt (Onboarding #8). Set during prefill apply.
  const [step1Mode, setStep1Mode] = useState<'lookup' | 'form'>('form');
  // Whether the generic-domain lookup prompt was ever shown. It already explains
  // that we have nothing to prefill, so the form's soft notice would just repeat it.
  const [lookupOffered, setLookupOffered] = useState(false);

  // Profile
  const [profile, setProfile] = useState<ProfileFormState>(EMPTY_PROFILE);

  // Modules
  const [modules, setModules] = useState<SetupModule[]>([]);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [recommendedModules, setRecommendedModules] = useState<string[]>([]);
  // Alpha modules the rules matched. Marked inside the step's experimental
  // section; deliberately never added to `selectedModules`.
  const [suggestedModules, setSuggestedModules] = useState<string[]>([]);
  const [hasAppliedRecommendations, setHasAppliedRecommendations] = useState(false);
  // Per-object_type default visibility mode chosen for configurable modules
  // (Sharing Infra P-4). Only sent for selected configurable modules on submit.
  const [visibilityModes, setVisibilityModes] = useState<Record<string, string>>({});

  // Demo data ("Load sample data?" step). Defaults to skip; DemoDataStep flips
  // it on once it knows the industry-recommended variation is available.
  const [demo, setDemo] = useState<DemoDataFormState>({ loadDemo: false, variation: null });
  const [isLoadingDemo, setIsLoadingDemo] = useState(false);

  const headingRef = useRef<HTMLHeadingElement>(null);
  const prefillApplied = useRef(false);
  // Set the moment a submit (create or join) begins. A successful submit calls
  // refreshAuth() — which populates user.tenant_id — and then navigates to
  // /onboarding; without this guard the "already has a tenant" redirect below
  // would race that refresh and bounce the user to "/" instead of the Hub.
  const completing = useRef(false);
  // Latched once the load effect has picked a flow (join / request / create).
  // The effect keys on `accessToken`, which rotates whenever the reactive-401
  // refresh in axiosInstance fires — re-running it then re-read
  // `has_domain_match_request` and dragged a user who had already escaped into
  // the create flow back to request-mode mid-wizard, so the final button POSTed
  // another join request instead of creating the tenant. The flow is a
  // per-mount decision; a token rotation must not re-decide it.
  const flowResolved = useRef(false);

  // Redirect away if the cached user already has a tenant (arrived at /setup by
  // mistake). Suppressed once a submit is in flight so the intended /onboarding
  // landing wins.
  useEffect(() => {
    if (user?.tenant_id && !completing.current) navigate('/', { replace: true });
  }, [user?.tenant_id, navigate]);

  // Fetch the data the create flow needs (module list + tenant-approval gate).
  // Shared by the initial load and the "create a workspace instead" escape from
  // the request-to-join path.
  const loadCreateEssentials = useCallback(async () => {
    const modulesData = await setupWizardApi.getModules();
    setModules(modulesData.modules);
    try {
      const gate = await setupWizardApi.getTenantApprovalRequired();
      setRequiresApproval(gate.require_tenant_approval);
    } catch (err) {
      // Non-fatal: fall back to the standard instant-create copy.
      console.warn('Failed to get tenant-approval gate state:', err);
    }
  }, []);

  // --- Load: verify pending_setup, branch the flow, fetch create essentials ---
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const fresh = await authApi.getMe(accessToken);
        if (cancelled) return;
        if (fresh.status !== 'pending_setup' || fresh.tenant_id) {
          await refreshAuth();
          navigate('/', { replace: true });
          return;
        }

        const options = await setupWizardApi.getOptions();
        if (cancelled) return;

        // Flow B (joiner short flow): an invitation or a domain match means the
        // tenant already exists, so the wizard collapses to a single Profile step
        // and admits the user via joinTenant. The same `has_invitation ||
        // has_domain_match` signal that used to branch create-vs-join now picks
        // the flow *length*. Invitation carries its company + admin flag directly;
        // a domain match admits into the tenant's primary (first) company — no
        // business-picker step (the join-admit path writes no tenant-level field).
        if (options.has_invitation && options.invitation) {
          const inv = options.invitation;
          flowResolved.current = true;
          setJoinTarget({
            tenantId: inv.tenant_id,
            companyId: inv.company_id,
            isAdmin: inv.is_admin,
            tenantName: inv.tenant_name,
          });
          setMode('join');
          return;
        }
        if (options.has_domain_match && options.domain_match) {
          const dm = options.domain_match;
          const companyId = dm.companies[0]?.id;
          if (companyId) {
            flowResolved.current = true;
            setJoinTarget({
              tenantId: dm.tenant_id,
              companyId,
              isAdmin: false,
              tenantName: dm.tenant_name,
            });
            setMode('join');
            return;
          }
          // Domain match with no joinable company (e.g. every company in the
          // tenant was deactivated). There is nothing to admit into, so fall
          // through to the create flow rather than block the user.
          console.warn('Domain match resolved no active company; falling through to tenant creation.');
        }
        if (options.has_domain_match_request && options.domain_match_request) {
          // Domain match with self-registration OFF: the user must request to
          // join and wait for admin approval. If an outstanding request already
          // exists, jump straight to the pending-approval screen.
          const existing = await setupWizardApi.getMyJoinRequest().catch((err) => {
            console.warn('Failed to get pending join request:', err);
            return null;
          });
          if (cancelled) return;
          flowResolved.current = true;
          if (existing) setPendingJoinRequest(existing);
          setRequestTarget(options.domain_match_request);
          setMode('request');
          return;
        }

        flowResolved.current = true;
        setMode('create');
        await loadCreateEssentials();
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to load setup wizard:', error);
        // eslint-disable-next-line no-restricted-syntax -- bare non-mutation call, no global handler fires
        showError(getErrorMessage(error));
        flowResolved.current = true;
        setMode('create');
      }
    }
    if (accessToken && !user?.tenant_id && !flowResolved.current) load();
    return () => {
      cancelled = true;
    };
    // Runs once per mount: `flowResolved` stops a token rotation (reactive-401
    // refresh) from re-deciding a flow the user is already walking, while a
    // rotation *during* the load still restarts it with the fresh token.
    // `user?.tenant_id` is intentionally omitted —
    // re-running on every auth refresh would re-fetch and could fight the wizard
    // state; the redirect-if-tenant guard lives in its own effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // --- Apply prefill into untouched fields (once) ---
  const applyPrefill = useCallback((pf: SetupWizardPrefill) => {
    if (prefillApplied.current) return;
    prefillApplied.current = true;
    const ed = editedCompany.current;
    const { values, confidence } = mapCompanyPrefill(pf.company);
    setPrefillOutcome(classifyPrefill(values));

    setCompany((prev) => {
      // Apply only the AI fields the user hasn't already touched.
      const applied = Object.fromEntries(
        Object.entries(values).filter(([field]) => !ed.has(field))
      ) as Partial<CompanyFormState>;
      const next = { ...prev, ...applied };
      // Fall back to detected default country if enrichment had none.
      if (!ed.has('country') && !next.country && pf.defaults.country_code)
        next.country = pf.defaults.country_code;
      return next;
    });

    setCompanyConfidence((prev) => {
      const next = { ...prev };
      (Object.keys(confidence) as CompanyField[]).forEach((field) => {
        if (!ed.has(field)) next[field] = confidence[field];
      });
      return next;
    });

    setCandidates(pf.person.candidates);
    setBestCandidateId(pf.person.best_candidate_id);

    // Generic-domain users have no enriched company (domain lane was skipped) —
    // open Step 1 in lookup mode so they can name their company or paste a URL.
    // Skipped once the user has started filling the form: prefill can now land
    // after the spinner released, and swapping the form out from under someone
    // mid-type would discard what they typed.
    if (pf.generic_domain && !pf.company.name.value && ed.size === 0) {
      setStep1Mode('lookup');
      setLookupOffered(true);
    }

    if (Object.keys(values).length > 0) setSrMessage(t('setupWizardV2.prefillAnnounce'));
  }, [t]);

  // --- Poll prefill (non-blocking) ---
  useEffect(() => {
    if (mode !== 'create') return;
    let cancelled = false;
    async function poll() {
      for (let attempt = 0; attempt < PREFILL_POLL_ATTEMPTS && !cancelled; attempt++) {
        // Stop blocking Step 1 once the spinner has had its say. We keep
        // polling in the background: enrichment's registration-time budget
        // outlives this window, and `applyPrefill` only fills fields the user
        // hasn't touched, so a late payload is safe to apply under the form.
        if (attempt === PREFILL_SPINNER_ATTEMPTS) setPrefillLoading(false);
        try {
          const pf = await setupWizardApi.getPrefill();
          if (cancelled) return;
          if (pf.status !== 'pending') {
            applyPrefill(pf);
            setPrefillLoading(false);
            return;
          }
        } catch {
          if (cancelled) return;
          setPrefillOutcome('none');
          setPrefillLoading(false);
          return;
        }
        await sleep(PREFILL_POLL_INTERVAL_MS);
        if (cancelled) return;
      }
      if (cancelled) return;
      // Budget exhausted with nothing applied: enrichment never landed.
      if (!prefillApplied.current) setPrefillOutcome('none');
      setPrefillLoading(false);
    }
    poll();
    return () => {
      cancelled = true;
    };
    // Polls once when `mode` becomes 'create'. accessToken/applyPrefill are stable
    // for the wizard lifetime; including them would needlessly restart the poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // --- Derive business-model flags from the (AI-prefilled or typed) industry ---
  // "Derived from industry where possible, asked only when ambiguous": the chips
  // open pre-selected from the industry signal until the user edits them, after
  // which `editedCompany` stops the sync and their choice stands.
  useEffect(() => {
    if (editedCompany.current.has('sellsFlags')) return;
    const derived = deriveSellsFlags(company.industry);
    setCompany((prev) => {
      const same = (Object.keys(derived) as (keyof typeof derived)[]).every(
        (k) => prev.sellsFlags[k] === derived[k]
      );
      return same ? prev : { ...prev, sellsFlags: derived };
    });
    if (Object.values(derived).some(Boolean)) {
      setCompanyConfidence((prev) => (prev.sellsFlags ? prev : { ...prev, sellsFlags: 'medium' }));
    }
  }, [company.industry]);

  // --- Seed operating countries with the HQ country until the user edits them ---
  useEffect(() => {
    if (editedCompany.current.has('operatingCountries')) return;
    if (company.isGlobalOperation) return;
    if (company.country && company.operatingCountries.length === 0) {
      const hq = company.country;
      setCompany((prev) => ({ ...prev, operatingCountries: [hq] }));
    }
    // Depend on the `.length` sentinel, not the array identity, so editing the
    // selection's contents doesn't re-trigger seeding; `editedCompany` is the
    // real edit guard. Same intentional dep-omission pattern as the effects above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.country, company.isGlobalOperation, company.operatingCountries.length]);

  // --- Gravatar preload: adopt as avatar if it resolves and none chosen yet ---
  useEffect(() => {
    if (mode !== 'create' || !user?.email) return;
    let active = true;
    getGravatarUrl(user.email).then((url) => {
      if (!url || !active) return;
      setGravatarUrl(url);
      const img = new Image();
      img.onload = () => {
        if (active) setProfile((p) => (p.photoUrl ? p : { ...p, photoUrl: url }));
      };
      img.src = url;
    });
    return () => {
      active = false;
    };
  }, [mode, user?.email]);

  // --- Load module recommendations when entering the Modules step ---
  useEffect(() => {
    // Leaving the step cancels an in-flight fetch. Without this, a fast
    // Modules→Back→edit answers→Modules loop can let the stale request resolve
    // last and overwrite the fresh recommendation — and `hasAppliedRecommendations`
    // would then be true, so nothing would ever correct it.
    let cancelled = false;
    async function loadRecommended() {
      if (activeStep !== 2 || hasAppliedRecommendations || modules.length === 0) return;
      const availableIds = modules.map((m) => m.id);
      try {
        const { recommended_modules, suggested_modules } =
          await setupWizardApi.getRecommendedModules({
            jobFunction: profile.jobFunction || null,
            jobLevel: profile.jobLevel || null,
            businessModels: selectedBusinessModels(company.sellsFlags),
            industry: company.industry || null,
            customerTypes: company.customerTypes,
            companySizeBand: company.sizeBand,
          });
        if (cancelled) return;
        setRecommendedModules(recommended_modules);
        setSuggestedModules(suggested_modules);
        setSelectedModules(recommended_modules.filter((id) => availableIds.includes(id)));
      } catch {
        if (cancelled) return;
        // The rules are unreachable (timeout, 5xx). Fall back to the modules the
        // rules recommend unconditionally rather than to the whole catalog: a
        // failed request must not sign a tenant up for every module we ship.
        setSelectedModules(FALLBACK_MODULE_IDS.filter((id) => availableIds.includes(id)));
      } finally {
        if (!cancelled) setHasAppliedRecommendations(true);
      }
    }
    loadRecommended();
    return () => {
      cancelled = true;
    };
    // Runs once when the user reaches the Modules step (guarded by
    // hasAppliedRecommendations). The company answers and job function/level are
    // read at call time, not depended on: changing either means stepping back,
    // and handleBack resets the guard so the next entry re-applies them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, hasAppliedRecommendations, modules]);

  // --- Focus the step heading on step change (focus management) ---
  useEffect(() => {
    headingRef.current?.focus();
  }, [activeStep, mode]);

  const patchCompany = useCallback((patch: Partial<CompanyFormState>) => {
    Object.keys(patch).forEach((k) => editedCompany.current.add(k));
    setCompany((prev) => ({ ...prev, ...patch }));
  }, []);

  const clearCompanyConfidence = useCallback((field: CompanyField) => {
    editedCompany.current.add(field);
    setCompanyConfidence((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // Generic-domain lookup: resolve a name/URL into a fresh company prefill and
  // switch Step 1 to the standard Confirm-Company form. Rejects on no match —
  // CompanyLookupPrompt catches it and drops the user to manual entry.
  const handleLookup = useCallback(
    async (query: string) => {
      const res = await setupWizardApi.fallbackResolve(query);
      const { values, confidence } = mapCompanyPrefill(res.company);
      // The user explicitly asked us to look this up — replace the form wholesale.
      editedCompany.current = new Set();
      setCompany(() => {
        const next: CompanyFormState = { ...EMPTY_COMPANY, ...values };
        if (!next.country && res.defaults.country_code) next.country = res.defaults.country_code;
        return next;
      });
      setCompanyConfidence(confidence);
      setPrefillOutcome(classifyPrefill(values));
      setStep1Mode('form');
      setSrMessage(t('setupWizardV2.prefillAnnounce'));
    },
    [t]
  );

  // Skip the lookup (or recover from a failed one) → manual Confirm-Company form.
  const handleManual = useCallback((typedName: string) => {
    if (typedName) {
      editedCompany.current.add('name');
      setCompany((prev) => ({ ...prev, name: typedName }));
    }
    setStep1Mode('form');
  }, []);

  const handleConfirmPerson = useCallback(
    (linkedinUrl: string, snippetHint: string | null) =>
      setupWizardApi.confirmPerson({
        linkedin_url: linkedinUrl,
        snippet_hint: snippetHint,
      }),
    []
  );

  const steps: WizardStep[] = useMemo(
    () => [
      { key: 'company', label: t('setupWizardV2.steps.company') },
      { key: 'profile', label: t('setupWizardV2.steps.profile') },
      { key: 'modules', label: t('setupWizardV2.steps.modules') },
      { key: 'demoData', label: t('setupWizardV2.steps.demoData') },
    ],
    [t]
  );

  const canProceed = () => {
    if (activeStep === 0) {
      // Lookup mode owns its own actions (the footer is hidden); the company-name
      // gate only applies once the standard Confirm-Company form is showing.
      if (step1Mode === 'lookup') return true;
      // The signup reason is the one answer a sysadmin cannot approve without,
      // so it gates the step alongside the company name. The other two signup
      // questions stay optional.
      return company.name.trim().length > 0 && company.signupReason.trim().length > 0;
    }
    return true; // Profile + Modules are optional/skippable.
  };

  const handleNext = () => {
    if (!canProceed()) return;
    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const handleBack = () => {
    if (activeStep === 2) setHasAppliedRecommendations(false);
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  // Flow B submit: admit the joiner into the existing tenant. The join-admit
  // path stamps onboarding_completed_at, seeds the Onboarding Hub, and writes
  // ONLY user-level fields (job role, modules, handle, timezone) — no tenant
  // write. Modules-beyond-invite-grants are out of scope, so enabled_modules is
  // null (inherit the invitation ceiling / tenant defaults).
  const handleJoinSubmit = async () => {
    if (!joinTarget) return;
    completing.current = true;
    setIsSubmitting(true);
    try {
      const result = await setupWizardApi.joinTenant({
        tenant_id: joinTarget.tenantId,
        company_id: joinTarget.companyId,
        is_admin: joinTarget.isAdmin,
        enabled_modules: null,
        job_function: profile.jobFunction || null,
        job_function_custom:
          profile.jobFunction === 'other' ? profile.jobFunctionCustom.trim() : null,
        job_level: profile.jobLevel || null,
        timezone: getBrowserTimezone(),
      });

      if (result.access_token && result.refresh_token) {
        localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, result.access_token);
        await setRefreshToken(result.refresh_token);
        await refreshAuth();
      }

      showSuccess(t('setupWizard.success'));
      navigate('/onboarding');
    } catch (error) {
      console.error('Failed to join tenant:', error);
      // eslint-disable-next-line no-restricted-syntax -- bare non-mutation call, no global handler fires
      showError(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Request-to-join submit: queue a pending request for an admin to approve.
  // On success the pending-approval screen takes over (via pendingJoinRequest).
  const handleSubmitJoinRequest = async () => {
    if (!requestTarget) return;
    setIsSubmitting(true);
    try {
      const result = await setupWizardApi.createJoinRequest({
        tenant_id: requestTarget.tenant_id,
        company_id: requestCompanyId || null,
      });
      setPendingJoinRequest(result);
    } catch (error) {
      console.error('Failed to create join request:', error);
      // eslint-disable-next-line no-restricted-syntax -- bare non-mutation call, no global handler fires
      showError(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Leave the request-to-join path and start the create flow instead. Used both
  // when abandoning before submitting and after cancelling a pending request.
  const switchToCreate = async () => {
    // The user's explicit choice outranks `has_domain_match_request`, which stays
    // true for the tenant's domain even after the request is cancelled.
    flowResolved.current = true;
    setPendingJoinRequest(null);
    setRequestTarget(null);
    setMode('create');
    try {
      await loadCreateEssentials();
    } catch (error) {
      console.error('Failed to load setup wizard:', error);
      // eslint-disable-next-line no-restricted-syntax -- bare non-mutation call, no global handler fires
      showError(getErrorMessage(error));
    }
  };

  // Cancel an already-submitted pending request, then fall back to create.
  const handleCancelJoinRequest = async () => {
    try {
      await setupWizardApi.cancelJoinRequest();
    } catch (error) {
      console.error('Failed to cancel join request:', error);
    }
    await switchToCreate();
  };

  const handleSubmit = async () => {
    completing.current = true;
    setIsSubmitting(true);
    try {
      // The photo the user picked on the Profile step travels inline: pre-tenant
      // they own no tenant, and `files.tenant_id` is NOT NULL, so the server
      // stores it in the same post-tenant write-back it already runs for
      // `avatar_photo_url`. That write-back precedes the tenant-approval gate,
      // so the photo survives a workspace held for review.
      const avatarPhotoDataUrl = profile.photoFile
        ? await readFileAsDataUrl(profile.photoFile)
        : null;

      const payload: CreateTenantRequest = {
        company_name: company.name.trim(),
        company_country: company.country,
        company_website: company.website.trim() || null,
        company_description: company.description.trim() || null,
        company_size_band: company.sizeBand,
        // Signup application answers (see CompanyFormState). The 'other' detail
        // only travels alongside an 'other' source — the backend drops it
        // otherwise, so sending it would be a lie the reviewer never sees.
        signup_reason: company.signupReason.trim() || null,
        signup_referral_source: company.referralSource,
        signup_referral_source_other:
          company.referralSource === 'other'
            ? company.referralSourceOther.trim() || null
            : null,
        signup_expected_users: company.expectedUsers,
        industry: company.industry.trim() || null,
        customer_types: company.customerTypes.length > 0 ? company.customerTypes : null,
        // Tenant-level fields formerly collected on /welcome — Flow A now carries
        // them so a new-tenant admin never visits /welcome.
        operating_countries: company.isGlobalOperation ? [] : company.operatingCountries,
        is_global_operation: company.isGlobalOperation,
        sells_physical_products: company.sellsFlags.sells_physical_products,
        sells_custom_made_goods: company.sellsFlags.sells_custom_made_goods,
        sells_services_by_project: company.sellsFlags.sells_services_by_project,
        sells_services_by_time: company.sellsFlags.sells_services_by_time,
        sells_subscriptions: company.sellsFlags.sells_subscriptions,
        sells_logistics_services: company.sellsFlags.sells_logistics_services,
        brand_color: company.brandColor,
        logo_file_id: company.logoFileId,
        enabled_modules: selectedModules.length > 0 ? selectedModules : null,
        // Sharing Infra P-4: an explicit mode per selected configurable module,
        // defaulted from the company size band. The backend still writes no row
        // for `everyone` — sending it keeps a deliberate `everyone` from being
        // re-derived as the band's restrictive default.
        visibility_modes: buildVisibilityModesPayload(
          modules,
          selectedModules,
          visibilityModes,
          company.sizeBand,
        ),
        job_function: profile.jobFunction || null,
        job_function_custom:
          profile.jobFunction === 'other' ? profile.jobFunctionCustom.trim() : null,
        job_level: profile.jobLevel || null,
        linkedin_url: profile.linkedinUrl || null,
        headline: profile.headline || null,
        avatar_file_id: profile.avatarFileId,
        avatar_photo_url: profile.photoUrl,
        avatar_photo_data_url: avatarPhotoDataUrl,
        timezone: getBrowserTimezone(),
        // The workspace inherits the language its creator was actually using.
        // Read from i18n rather than from the account preference: the wizard
        // renders a LanguageSelector on every step, and a signup that never
        // touched one leaves preferred_language NULL — which is what used to
        // hand an English-speaking Israeli founder a Hebrew workspace.
        default_language: toTenantDefaultLanguage(i18n.language),
      };

      const result = await setupWizardApi.createTenant(payload);

      // Tenant-approval gate held the workspace for review — no tokens issued.
      // Land on the "application received" screen instead of the app. Drop the
      // now-useless pending_setup token from storage so it can't be replayed on
      // reload/back-nav (the user is now pending_approval; the create path
      // rejects a second submit anyway). Keep the in-memory auth context so this
      // screen renders and its "Back to sign in" can still revoke server-side.
      if (result.pending_approval || !result.access_token || !result.refresh_token) {
        localStorage.removeItem(AUTH_STORAGE_KEYS.accessToken);
        await clearRefreshToken();
        setSubmitted(true);
        return;
      }

      localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, result.access_token);
      await setRefreshToken(result.refresh_token);
      await refreshAuth();

      // Optionally load demo data into the freshly-created tenant (§4.4-4.5). The
      // tenant now exists and the caller is its admin (new token in storage).
      // Best-effort: a failure (e.g. a variation whose golden tenant isn't
      // authored yet) must NOT block onboarding — the user can load it later
      // from Settings.
      if (demo.loadDemo && demo.variation) {
        setIsLoadingDemo(true);
        try {
          await setupWizardApi.provisionDemo(demo.variation);
        } catch (err) {
          // Non-fatal: the tenant exists; surface a soft notice and let the user
          // load it later from Settings.
          console.warn('Demo data provisioning failed (non-fatal):', err);
          // eslint-disable-next-line no-restricted-syntax -- bare non-mutation call, no global handler fires
          showError(t('setupWizardV2.demoData.loadFailed'));
        } finally {
          setIsLoadingDemo(false);
        }
      }

      showSuccess(t('setupWizard.success'));
      navigate('/onboarding');
    } catch (error) {
      console.error('Failed to complete setup:', error);
      // eslint-disable-next-line no-restricted-syntax -- bare non-mutation call, no global handler fires
      showError(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return <ApplicationReceivedScreen email={user?.email ?? ''} companyName={company.name.trim()} />;
  }

  // An outstanding request-to-join short-circuits to the pending-approval screen.
  if (pendingJoinRequest) {
    return (
      <JoinRequestPendingScreen
        tenantName={pendingJoinRequest.tenant_name ?? requestTarget?.tenant_name ?? ''}
        email={user?.email ?? ''}
        onCancel={handleCancelJoinRequest}
      />
    );
  }

  // Request-to-join (domain match, self-registration OFF): pick an optional
  // company, then submit the request for an administrator to approve.
  if (mode === 'request' && requestTarget) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4 py-8">
        <Card className="w-full max-w-[600px] shadow-lg">
          <CardContent className="space-y-6 p-4 sm:p-6 md:p-8">
            <div className="flex items-center justify-between mb-2">
              <WizardLogoutButton />
              <LanguageSelector variant="labeled" />
            </div>
            <div className="space-y-2 text-center">
              <h1 className={STANDALONE_TITLE}>
                {t('setupWizard.requestToJoin', { tenant: requestTarget.tenant_name })}
              </h1>
              <p className="text-sm text-muted-foreground sm:text-base">
                {t('setupWizard.requestToJoinScreenSubtitle')}
              </p>
            </div>

            {requestTarget.companies.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="requestCompany">{t('setupWizard.requestCompanyLabel')}</Label>
                <Select value={requestCompanyId} onValueChange={setRequestCompanyId}>
                  <SelectTrigger id="requestCompany" className="h-11">
                    <SelectValue placeholder={t('setupWizard.requestCompanyPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {requestTarget.companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t('setupWizard.requestCompanyHint')}
                </p>
              </div>
            )}

            <div className="flex flex-col-reverse gap-2 border-t pt-6 sm:flex-row sm:items-center sm:justify-between">
              <Button
                type="button"
                variant="ghost"
                className="h-11 text-muted-foreground"
                onClick={switchToCreate}
                disabled={isSubmitting}
              >
                {t('setupWizard.createNew')}
              </Button>
              <Button
                type="button"
                className="h-11"
                onClick={handleSubmitJoinRequest}
                disabled={isSubmitting}
                data-testid="wizard-request-submit"
              >
                {isSubmitting && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                {t('setupWizard.requestToJoinSubmit')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Flow B — joiner short flow: a single Profile step, then admit.
  if (mode === 'join' && joinTarget) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4 py-8">
        <Card className="w-full max-w-[600px] shadow-lg">
          <CardContent className="space-y-6 p-4 sm:p-6 md:p-8">
            <div className="flex items-center justify-between mb-2">
              <WizardLogoutButton />
              <LanguageSelector variant="labeled" />
            </div>
            <div className="space-y-2 text-center">
              <h1 className={STANDALONE_TITLE}>
                {t('setupWizardV2.join.title', { tenant: joinTarget.tenantName })}
              </h1>
              <p className="text-sm text-muted-foreground sm:text-base">
                {t('setupWizardV2.join.subtitle')}
              </p>
            </div>

            <div className="min-h-[280px]">
              <h2
                ref={headingRef}
                tabIndex={-1}
                className="mb-4 text-lg font-semibold outline-none sm:text-xl"
              >
                {t('setupWizardV2.join.profileHeading')}
              </h2>
              <JoinProfileStep
                firstName={user?.first_name ?? ''}
                lastName={user?.last_name ?? ''}
                email={user?.email ?? ''}
                value={profile}
                onChange={(patch) => setProfile((p) => ({ ...p, ...patch }))}
              />
            </div>

            <div className="flex items-center justify-end border-t pt-6">
              <Button
                className="h-11"
                onClick={handleJoinSubmit}
                disabled={isSubmitting}
                data-testid="wizard-join-submit"
              >
                {isSubmitting && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                {t('setupWizardV2.getStarted')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (mode === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const isLastStep = activeStep === steps.length - 1;
  // Generic-domain lookup owns its own actions — hide the wizard's footer nav.
  const inLookup = activeStep === 0 && step1Mode === 'lookup';
  // There is nothing to "confirm" or call "good" unless the AI filled the core
  // fields — Step 1's heading and CTA both defer to what enrichment returned.
  const companyEnriched = prefillOutcome === 'enriched';
  const prefillFailed = prefillOutcome === 'none' && !lookupOffered;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4 py-8">
      <Card className="w-full max-w-[600px] shadow-lg">
        <CardContent className="space-y-6 p-4 sm:p-6 md:p-8">
          <div className="flex items-center justify-between mb-2">
            <WizardLogoutButton />
            <LanguageSelector variant="labeled" />
          </div>
          <div className="space-y-2 text-center">
            <h1 className={STANDALONE_TITLE}>{t('setupWizardV2.title')}</h1>
            <p className="text-sm text-muted-foreground sm:text-base">
              {t('setupWizardV2.subtitle')}
            </p>
          </div>

          <WizardStepper steps={steps} activeStep={activeStep} />

          {/* Disclose the approval requirement up front, not only on the final
              create step — so applicants aren't surprised at the end. */}
          {requiresApproval && !isLastStep && (
            <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{t('setupWizard.approvalNotice')}</span>
            </div>
          )}

          {prefillLoading && (
            <AIProcessingBanner message={t('setupWizardV2.settingUp')} />
          )}

          {isLoadingDemo && (
            <AIProcessingBanner message={t('setupWizardV2.demoData.loading')} />
          )}

          {/* Screen-reader announcements for prefill + step changes. */}
          <div aria-live="polite" className="sr-only">
            {srMessage}
          </div>

          <div className="min-h-[280px]">
            <h2
              ref={headingRef}
              tabIndex={-1}
              className="mb-4 text-lg font-semibold outline-none sm:text-xl"
            >
              {/* Held back until prefill resolves so the heading flips to lookup
                  mode in lockstep with the body — no "Confirm company" flash. */}
              {activeStep === 0 &&
                !prefillLoading &&
                (inLookup
                  ? t('setupWizardV2.lookup.heading')
                  : t(
                      companyEnriched
                        ? 'setupWizardV2.company.heading'
                        : 'setupWizardV2.company.headingBlank'
                    ))}
              {activeStep === 1 && t('setupWizardV2.profile.heading')}
              {activeStep === 2 && t('setupWizardV2.modules.heading')}
              {activeStep === 3 && t('setupWizardV2.demoData.heading')}
            </h2>

            {/* Step 1 waits for prefill before deciding lookup vs. form, so a
                generic-domain user never flashes the full company form first. */}
            {activeStep === 0 && prefillLoading && <CompanyStepSkeleton />}
            {activeStep === 0 && !prefillLoading && (
              inLookup ? (
                <CompanyLookupPrompt onResolve={handleLookup} onManual={handleManual} />
              ) : (
                <CompanyStep
                  value={company}
                  onChange={patchCompany}
                  confidence={companyConfidence}
                  onClearConfidence={clearCompanyConfidence}
                  prefillFailed={prefillFailed}
                />
              )
            )}
            {activeStep === 1 && (
              <ProfileStep
                firstName={user?.first_name ?? ''}
                lastName={user?.last_name ?? ''}
                email={user?.email ?? ''}
                gravatarUrl={gravatarUrl}
                candidates={candidates}
                bestCandidateId={bestCandidateId}
                value={profile}
                onChange={(patch) => setProfile((p) => ({ ...p, ...patch }))}
                onConfirmPerson={handleConfirmPerson}
              />
            )}
            {activeStep === 2 && (
              <ModulesStep
                modules={modules}
                selectedModules={selectedModules}
                recommendedModules={recommendedModules}
                suggestedModules={suggestedModules}
                hasRoleInput={Boolean(profile.jobFunction || profile.jobLevel)}
                onChange={setSelectedModules}
                visibilityModes={visibilityModes}
                onVisibilityChange={(objectType, mode) =>
                  setVisibilityModes((prev) => ({ ...prev, [objectType]: mode }))
                }
                defaultVisibilityMode={defaultVisibilityModeForBand(company.sizeBand)}
              />
            )}
            {activeStep === 3 && (
              <DemoDataStep
                industry={company.industry.trim()}
                value={demo}
                onChange={(patch) => setDemo((d) => ({ ...d, ...patch }))}
              />
            )}
          </div>

          {isLastStep && requiresApproval && (
            <p className="rounded-lg border border-border bg-muted/40 p-3 text-center text-sm text-muted-foreground">
              {t('setupWizard.approvalNotice')}
            </p>
          )}

          {!inLookup && (
          <div className="flex items-center justify-between border-t pt-6">
            <div>
              {activeStep > 0 && (
                <Button
                  variant="outline"
                  className="h-11"
                  onClick={handleBack}
                  disabled={isSubmitting}
                >
                  {t('common:app.back')}
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {activeStep === 1 && (
                <Button
                  variant="ghost"
                  className="h-11"
                  onClick={handleNext}
                  disabled={isSubmitting}
                >
                  {t('common:app.skip')}
                </Button>
              )}
              {!isLastStep ? (
                <Button
                  className="h-11 gap-1.5"
                  onClick={handleNext}
                  disabled={!canProceed()}
                  data-testid="wizard-next-step"
                >
                  {activeStep !== 0
                    ? t('common:app.next')
                    : companyEnriched
                      ? t('setupWizardV2.company.cta')
                      : t('common:app.continue')}
                  <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                </Button>
              ) : (
                <Button
                  className="h-11"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  data-testid="wizard-create-submit"
                >
                  {isSubmitting && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                  {requiresApproval
                    ? t('setupWizard.requestWorkspace')
                    : t('setupWizardV2.getStarted')}
                </Button>
              )}
            </div>
          </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
